import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import LoadingState from "../components/LoadingState";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Search, RefreshCcw } from "lucide-react";
import api from "../lib/api";
import { downloadAuthenticated } from "../lib/download";
import { useAuth } from "../lib/auth";

const ACTION_LABELS = {
    "auth.login": "Accesso",
    "auth.logout": "Logout",
    "auth.login_failed": "Accesso fallito",
    "user.created": "Utente creato",
    "user.updated": "Utente aggiornato",
    "user.password_changed": "Password cambiata",
    "user.password_reset_requested": "Reset password richiesto",
    "user.password_reset_confirmed": "Reset password confermato",
    "user.company_assigned": "Assegnato ad azienda",
    "user.company_revoked": "Rimosso da azienda",
    "user.role_changed": "Ruolo modificato",
    "user.mfa_enabled": "MFA abilitato",
    "user.mfa_disabled": "MFA disabilitato",
    "company.created": "Azienda creata",
    "company.updated": "Azienda aggiornata",
    "company.deleted": "Azienda eliminata",
};

function formatDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        const hh = String(d.getHours()).padStart(2, "0");
        const mi = String(d.getMinutes()).padStart(2, "0");
        return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
    } catch { return iso; }
}

export default function AuditLogPage() {
    const { isSuperAdmin, companyId } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterAction, setFilterAction] = useState("");
    const [filterUser, setFilterUser] = useState("");
    // Super Admin toggle: "all" (global endpoint) | "company" (company-scoped)
    const [scope, setScope] = useState(
        isSuperAdmin() && companyId === "__all__" ? "all" : "company"
    );

    // Derive effective scope synchronously (avoids state-cascade races):
    // - Non-super-admin: always company-scoped
    // - Super Admin: forced to "all" when CompanySelector == __all__
    //                otherwise honour the explicit toggle
    const effectiveScope = !isSuperAdmin()
        ? "company"
        : companyId === "__all__"
            ? "all"
            : scope;
    const useGlobal = isSuperAdmin() && effectiveScope === "all";

    // Keep toggle UI reflecting reality when CompanySelector changes.
    // Note: this setState alone must NOT drive the load effect (deps below use
    // companyId + effectiveScope directly), preventing the previous race.
    useEffect(() => {
        if (!isSuperAdmin()) return;
        if (companyId === "__all__" && scope !== "all") setScope("all");
        if (companyId !== "__all__" && scope === "all") setScope("company");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId]);

    // Single fetch effect with abort — cancels any in-flight request when deps change.
    useEffect(() => {
        const controller = new AbortController();
        const endpoint = useGlobal ? "/admin/audit-logs" : "/company/audit-logs";
        const params = new URLSearchParams();
        if (filterAction) params.append("action", filterAction);
        if (filterUser) params.append("user_id", filterUser);
        params.append("limit", "200");

        setLoading(true);
        api.get(`${endpoint}?${params.toString()}`, { signal: controller.signal })
            .then(({ data }) => setItems(data?.items || []))
            .catch((err) => {
                if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
                toast.error(err?.response?.data?.detail || "Errore nel caricamento log");
                setItems([]);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, useGlobal]);

    const load = () => {
        // Manual refresh: bump a nonce by toggling scope then back? Simpler: refetch inline.
        const controller = new AbortController();
        const endpoint = useGlobal ? "/admin/audit-logs" : "/company/audit-logs";
        const params = new URLSearchParams();
        if (filterAction) params.append("action", filterAction);
        if (filterUser) params.append("user_id", filterUser);
        params.append("limit", "200");
        setLoading(true);
        api.get(`${endpoint}?${params.toString()}`, { signal: controller.signal })
            .then(({ data }) => setItems(data?.items || []))
            .catch((err) => {
                if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
                toast.error(err?.response?.data?.detail || "Errore nel caricamento log");
                setItems([]);
            })
            .finally(() => setLoading(false));
    };

    const columns = [
        {
            key: "timestamp",
            header: "Data / Ora",
            sortable: true,
            render: (r) => (
                <span className="text-xs font-mono text-[#172033]">{formatDate(r.timestamp)}</span>
            ),
        },
        {
            key: "action",
            header: "Azione",
            sortable: true,
            render: (r) => (
                <div>
                    <div className="text-sm font-medium text-[#172033]">
                        {ACTION_LABELS[r.action] || r.action}
                    </div>
                    <div className="text-[10px] text-[#64748B] uppercase tracking-wider">{r.action}</div>
                </div>
            ),
        },
        {
            key: "user_id",
            header: "Utente",
            render: (r) => (
                <span className="text-xs font-mono text-[#64748B]" title={r.user_id || ""}>
                    {r.user_id ? r.user_id.slice(0, 8) + "…" : "—"}
                </span>
            ),
        },
        {
            key: "object_type",
            header: "Oggetto",
            render: (r) => r.object_type || "—",
        },
        {
            key: "ip",
            header: "IP",
            render: (r) => <span className="text-xs font-mono">{r.ip || "—"}</span>,
        },
        {
            key: "note",
            header: "Dettagli",
            render: (r) => {
                const note = r.note || "";
                if (!note) return <span className="text-xs text-[#94A3B8]">—</span>;
                const truncated = note.length > 60 ? note.slice(0, 60) + "…" : note;
                return (
                    <span
                        className="text-xs text-[#64748B] cursor-help"
                        title={note}
                        data-testid={`audit-note-${r.id || ""}`}
                    >
                        {truncated}
                    </span>
                );
            },
        },
    ];

    return (
        <div className="space-y-6">
            <PageHeader
                title="Audit log"
                subtitle="Registro immutabile di tutte le operazioni di scrittura. I record vengono inseriti e non modificati."
                actions={
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            data-testid="audit-export-csv"
                            onClick={() => downloadAuthenticated("/company/audit-logs/export/csv", "audit_log.csv")}
                            className="gap-2"
                        >
                            Esporta CSV
                        </Button>
                        <Button
                            variant="outline"
                            onClick={load}
                            data-testid="audit-refresh-btn"
                            className="gap-2"
                        >
                            <RefreshCcw className="w-4 h-4" />
                            Aggiorna
                        </Button>
                    </div>
                }
            />

            {isSuperAdmin() && (
                <div
                    data-testid="audit-scope-toggle"
                    className="inline-flex items-center bg-white border border-[#DCE3EE] rounded-lg p-1 shadow-sm"
                >
                    <button
                        data-testid="audit-scope-all"
                        onClick={() => setScope("all")}
                        className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-colors ${scope === "all"
                            ? "bg-[#2563EB] text-white"
                            : "text-[#64748B] hover:text-[#172033]"
                            }`}
                    >
                        Tutti gli eventi
                    </button>
                    <button
                        data-testid="audit-scope-company"
                        onClick={() => setScope("company")}
                        disabled={companyId === "__all__"}
                        className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-colors ${scope === "company"
                            ? "bg-[#2563EB] text-white"
                            : "text-[#64748B] hover:text-[#172033] disabled:opacity-40 disabled:cursor-not-allowed"
                            }`}
                        title={companyId === "__all__" ? "Seleziona un'azienda specifica per usare questa modalità" : ""}
                    >
                        Solo azienda corrente
                    </button>
                </div>
            )}

            <div className="bg-white border border-[#DCE3EE] rounded-xl p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                        <Label htmlFor="filter-action" className="text-xs">Azione</Label>
                        <Input
                            id="filter-action"
                            data-testid="audit-filter-action"
                            value={filterAction}
                            onChange={(e) => setFilterAction(e.target.value)}
                            placeholder="es. auth.login"
                            className="mt-1 h-9"
                        />
                    </div>
                    <div>
                        <Label htmlFor="filter-user" className="text-xs">User ID</Label>
                        <Input
                            id="filter-user"
                            data-testid="audit-filter-user"
                            value={filterUser}
                            onChange={(e) => setFilterUser(e.target.value)}
                            placeholder="UUID utente"
                            className="mt-1 h-9 font-mono text-xs"
                        />
                    </div>
                    <div className="flex items-end">
                        <Button
                            onClick={load}
                            data-testid="audit-apply-filters-btn"
                            className="w-full h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white gap-2"
                        >
                            <Search className="w-4 h-4" />
                            Applica filtri
                        </Button>
                    </div>
                </div>
            </div>

            {loading ? (
                <LoadingState />
            ) : (
                <DataTable
                    testId="audit-log-table"
                    columns={columns}
                    data={items}
                    pageSize={20}
                    emptyTitle="Nessun evento registrato"
                    emptyDescription="Il registro è vuoto o nessun evento corrisponde ai filtri."
                />
            )}
        </div>
    );
}
